import { useCallback, useEffect, useState } from 'react';
import { billingApi } from '@/api';
import { listPendingSales, removePendingSale, type PendingPosSale } from '@/utils/posOfflineQueue';
import { Button } from '@/components/ui/Button';
import { notify } from '@/core/toast/notify';

export function PosOfflineQueueBanner() {
  const [pending, setPending] = useState<PendingPosSale[]>([]);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => {
    setPending(await listPendingSales());
  }, []);

  useEffect(() => {
    void refresh();
    const onOnline = () => void refresh();
    window.addEventListener('online', onOnline);
    const id = window.setInterval(() => void refresh(), 15_000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.clearInterval(id);
    };
  }, [refresh]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const sales = await listPendingSales();
      for (const sale of sales) {
        try {
          await billingApi.createInvoice(sale.body);
          await removePendingSale(sale.clientRequestId);
        } catch {
          break;
        }
      }
      await refresh();
      notify.success('Offline queue synced');
    } finally {
      setSyncing(false);
    }
  };

  if (pending.length === 0) return null;

  return (
    <div className="flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
      <span>
        {pending.length} sale{pending.length > 1 ? 's' : ''} waiting to sync
        {!navigator.onLine && ' (offline)'}
      </span>
      <Button
        type="button"
        size="sm"
        disabled={syncing || !navigator.onLine}
        loading={syncing}
        onClick={() => void syncNow()}
      >
        Sync now
      </Button>
    </div>
  );
}
